import { createServerFn } from "@tanstack/react-start";

import { authMiddleware, mondayBoards } from "@onesolution/middlewares";

import { TASKS_COLUMNS } from "@onesolution/constants";
import { PROJECT_COLUMNS } from "@onesolution/constants/monday/projects";
import type { MONDAY_TYPES } from "@onesolution/monday";
import { mondayClient } from "@onesolution/monday/monday";
import { endOfDay, startOfDay } from "date-fns";
import z from "zod";
import { getTimelineRange } from "./getOperationPlanningBase";

const getPlanningOperationSubitemSchema = z.object({
	itemIds: z.array(z.string()),
	depth: z.number(),
});

export const getPlanningOperationSubitemFromServer = createServerFn()
	.middleware([authMiddleware, mondayBoards])
	.validator(getPlanningOperationSubitemSchema)
	.handler(async ({ data }) => {
		if (data.depth === 1) {
			const products = await mondayClient.query.items<
				MONDAY_TYPES["base"] & {
					timeline: MONDAY_TYPES["text"];
					subitems: { id: string }[];
					status: MONDAY_TYPES["status"];
				}
			>({
				itemIds: data.itemIds,
				columnIds: [PROJECT_COLUMNS.timeline, PROJECT_COLUMNS.status],
				extraQuery: "subitems { id }",
			});

			return products.map((prod) => ({
				id: Number(prod.id),
				text: prod.name,
				...getTimelineRange(prod.timeline),
				status: prod.status,
				type: prod.status?.label?.replace(/\s+/g, "").toLocaleLowerCase(),
				subIds: prod.subitems.map((s) => s.id),
			}));
		}

		if (data.depth === 2) {
			const tasks = await mondayClient.query.items<
				MONDAY_TYPES["base"] & {
					deadline: MONDAY_TYPES["date"];
					status: MONDAY_TYPES["status"];
					visibility: MONDAY_TYPES["checkbox"];
				}
			>({
				itemIds: data?.itemIds,
				columnIds: [
					TASKS_COLUMNS.name,
					TASKS_COLUMNS.status,
					TASKS_COLUMNS.deadline,
					TASKS_COLUMNS.visibility,
				],
			});

			return tasks
				.filter((task) => task.visibility)
				.map((task) => {
					return {
						id: Number(task.id),
						text: task.name,
						status: task.status,
						start: task.deadline
							? startOfDay(new Date(task.deadline)).toISOString()
							: null,
						end: task.deadline
							? endOfDay(new Date(task.deadline)).toISOString()
							: null,
						type: task.status.label?.replace(/\s+/g, "").toLocaleLowerCase(),
						subIds: null,
					};
				});
		}
	});
